import styles from './sign-form.module.css';
import classNames from 'classnames/bind';
import { useForm } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';
import { validate } from '../../../utils';
import { SignInput } from '../sign-input';
import { Button } from '../../Buttons';
import Loader from '../../Loaders/Loader';
import { GlobalService } from '../../../hexagonal-architecture-frontend-base/src/domain/services/Global.service';
import { FC, useEffect, useRef, useState } from 'react';
import type { UserSignData } from 'src/ts/interfaces';
import {
  useSelectedPatient,
  useSelectedQuote,
  useSession,
  useShowErrorPopup,
  useUpdateQuote,
  useUserSignData,
  useResolvedLuraSignUserData,
  useCreateLuraSignUser,
} from '../../../hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { useCreateHtmlSignDocument } from '../../../hooks/documents';
import { ConsentHtmlSignDocumentData } from 'src/ts/interfaces/documents/consent-html-sign-document-data';
import { documentsData } from '../../../consts/pdf-documents/documents';

interface DataToHold {
  consentId: string;
  contactConsentId: string;
  signedAuthorizations: boolean;
  dealId: string;
  dealName: string;
  patientId: string;
  patientName: string;
  accessToConsentSignForm?: boolean;
  luraSignUserId?: string;
  email?: string;
}

const cx = classNames.bind(styles);

/* =========================
     GET GEOLOCATION, IP AND USERAGENT
  ========================= */
const getClientMetadata = async () => {
  let geoLocation = {};
  let ipAddress = '';
  const userAgent = navigator.userAgent;

  // Geolocation
  if (navigator.geolocation) {
    try {
      geoLocation = await new Promise(resolve => {
        navigator.geolocation.getCurrentPosition(
          pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
          () => resolve({}),
        );
      });
    } catch (error) {
      console.error('Error retrieving gelocation: ', error);
    }
  }

  // IP fetch from public API
  try {
    const res = await fetch('https://api.ipify.org?format=json');
    const data = await res.json();
    ipAddress = data.ip || '';
  } catch (error) {
    console.error('Error retrieving ip: ', error);
  }

  return { geoLocation, ipAddress, userAgent };
};

export const NewSignForm: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { patientId, reportId } = useParams();
  const { selectedPatient } = useSelectedPatient();
  const { saveUserSignDataAsync, userSignData } = useUserSignData();
  const { userInfo, refreshUser } = useSession();
  const { luraSignUserData, isLoading } = useResolvedLuraSignUserData(userInfo?.zohoId);
  const { selectedQuote } = useSelectedQuote();
  const { setUserInfo } = useSession();
  const { updateQuote } = useUpdateQuote();
  const { createLuraSignUserAsync } = useCreateLuraSignUser();
  const { createHtmlSignDocument } = useCreateHtmlSignDocument();
  const { showErrorPopup } = useShowErrorPopup();
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    const checkUser = async () => {
      if (userInfo && !userInfo?.zohoId) await refreshUser();
    };
    checkUser();
  }, [userInfo]);

  const dataToHoldRef = useRef<DataToHold>({
    consentId: selectedPatient.dealDetails.Archivo_Consentimientos?.length
      ? selectedPatient.dealDetails.Archivo_Consentimientos[0].id
      : '',
    contactConsentId: selectedPatient.Consentimientos?.length
      ? selectedPatient.Consentimientos[0].id
      : '',
    signedAuthorizations: selectedPatient.dealDetails.Autorizaciones_Firmadas,
    dealId: selectedPatient.dealDetails.id,
    dealName: selectedPatient.dealDetails.Deal_Name,
    patientId: selectedPatient.id,
    patientName: selectedPatient.title,
    accessToConsentSignForm: selectedQuote?.Access_To_Consent_Sign_Form,
  });

  useEffect(() => {
    if (selectedQuote?.id && !dataToHoldRef.current.accessToConsentSignForm) {
      updateQuote.accessToConsentSignForm(selectedQuote.id, true);
      dataToHoldRef.current.accessToConsentSignForm = true; 
    }
  }, [selectedQuote]);

  useEffect(() => {
    if (!luraSignUserData) return;

    dataToHoldRef.current = {
      ...dataToHoldRef.current,
      luraSignUserId: luraSignUserData.id,
      email: luraSignUserData.email,
    };
  }, [luraSignUserData]);

  const form = useForm({
    defaultValues: {
      email: userSignData.email,
      tutorFullName: userSignData.tutorFullName,
      tutorCountryId: userSignData.tutorCountryId,
      patientCountryId: userSignData.patientCountryId,
      patientId: userSignData.patientId,
    },
    onSubmit: async (values) => {
      const currentQuoteId = selectedQuote?.id;

      if (!currentQuoteId || isRedirecting) return;

      // let luraSignId = dataToHoldRef.current.luraSignUserId;
      let finalLuraSignId = luraSignUserData?.id;

      if(!finalLuraSignId) {

        const newLuraSignUser = await createLuraSignUserAsync({
          email: values.value.email,
          phone: userInfo?.phone,
          tutorFullName: values.value.tutorFullName,
          tutorCountryId: values.value.tutorCountryId,
          zohoId: userInfo?.zohoId,
        });

        finalLuraSignId = newLuraSignUser?.id;
        dataToHoldRef.current.luraSignUserId = finalLuraSignId;
      }
    
      if (!finalLuraSignId) {
        showErrorPopup('errors.luraSignUserNotLoaded');
        return;
      }

      setIsRedirecting(true);
      
      try {
        updateQuote.accessToConsentSignDocument(currentQuoteId, true);

        // 3. Guardado de datos y Metadatos (en paralelo para ganar tiempo)
        const [userDataResponse, metadata] = await Promise.all([
          saveUserSignDataAsync({
            patientId: userSignData.patientId,
            tutorId: userInfo?.id ?? 0,
            quoteId: selectedQuote?.id ?? '',
            data: {
              patientId: userSignData.patientId,
              email: values.value.email,
              tutorFullName: values.value.tutorFullName,
              tutorCountryId: values.value.tutorCountryId,
              patientCountryId: values.value.patientCountryId,
            },
          }),
          getClientMetadata()
        ]);

        const { geoLocation, ipAddress, userAgent } = metadata;

        // 4. Actualizar estado local de Stripe si existe
        if (userInfo && (userDataResponse as UserSignData).stripeId) {
          setUserInfo({ ...userInfo, stripeId: (userDataResponse as UserSignData).stripeId });
        }

        // 5. Creación del documento
        createHtmlSignDocument(
          {
            userId: finalLuraSignId,
            documentByCountryId: documentsData['INFORMED_CONSENT']['es-ES'].documentId,
            ipAddress,
            userAgent,
            geoLocation,
            metadata: {},
            data: {
              tutorFullName: values.value.tutorFullName,
              tutorCountryId: values.value.tutorCountryId,
              patientCountryId: values.value.patientCountryId,
              patientId: userSignData.patientId,
              patientName: selectedPatient.title,
              tutorEmail: values.value.email,
              carehomeName: selectedPatient.residence,
              quoteId: selectedQuote?.id ?? '',
              dealId: selectedPatient.dealDetails.id,
            },
            locale: 'es-ES',
          },
          {
            onError: error => {
              setIsRedirecting(false);
              showErrorPopup(`errors.${error.message}`);
            },
            onSuccess: async (consentHtmlSignDocumentCreated) => {
              const docData = consentHtmlSignDocumentCreated as ConsentHtmlSignDocumentData;
              
              await refreshUser(); 

              if (docData.isOk) {
                navigate(`/informeDetallado/${patientId}/${reportId}/sign-document/${docData.htmlDocumentId}`);
              } else {
                navigate(`/informeDetallado/${patientId}/${reportId}/sign-document/`);
              }
            },
          }
        );
      } catch (error: any) {
        setIsRedirecting(false);
        console.error('SignForm - FormSubmit(): ', error);
      }
    },
  });

  if(userSignData) console.log('USER SIGN DATA IN SIGN FORM', userSignData);

  const [isInitialLoadDone, setIsInitialLoadDone] = useState(false);

  useEffect(() => {
    if (userSignData && userSignData.patientCountryId && !isInitialLoadDone) {
      form.setFieldValue('email', userSignData.email || '');
      form.setFieldValue('tutorFullName', userSignData.tutorFullName || '');
      form.setFieldValue('tutorCountryId', userSignData.tutorCountryId || '');
      form.setFieldValue('patientCountryId', userSignData.patientCountryId || '');
      setIsInitialLoadDone(true);
    }
  }, [userSignData, form, isInitialLoadDone]);

  const hideDocument = true;
  const isLoadingSignForm = false;

  // return hideDocument && !isAllSignDataAvailable ? (
  return hideDocument && !isLoadingSignForm ? (
    <>
      <p className='max-w-xl font-bold'>
        {`${GlobalService.uppercaseAfterPointFirstsLetters(t('pages.pagos.formDescription'))}:`}
      </p>
      <form
        className={cx('sign-form')}
        onSubmit={e => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <form.Field
          name={'email'}
          validators={{
            onChange: ({ value }) => {
              const isValid = validate(value, 'email');
              return isValid ? undefined : 'errors.emailInvalid';
            },
          }}
          // eslint-disable-next-line react/no-children-prop
          children={field => {
            return (
              <SignInput
                id={field.name}
                label={'email'}
                placeholder=''
                type={'text'}
                // type={userSignData.email === '' ? 'text' : 'hidden'}
                value={field.state.value}
                onChange={field.handleChange}
                onBlur={field.handleBlur}
                errorMessage={field.state.meta.errors.join(', ')}
              />
            );
          }}
        />

        <form.Field
          name={'tutorFullName'}
          validators={{
            onChange: ({ value }) => {
              const isValid = value === '' ? false : true;
              return isValid ? undefined : 'errors.tutorFullNameInvalid';
            },
          }}
          // eslint-disable-next-line react/no-children-prop
          children={field => {
            return (
              <SignInput
                id={field.name}
                label={`${GlobalService.uppercaseFirstLetter(t('pages.pagos.tutorFullName'))}`}
                placeholder=''
                type={'text'}
                // type={userSignData.tutorFullName === '' ? 'text' : 'hidden'}
                value={field.state.value}
                onChange={field.handleChange}
                onBlur={field.handleBlur}
                errorMessage={field.state.meta.errors.join(', ')}
              />
            );
          }}
        />

        <form.Field
          name={'tutorCountryId'}
          validators={{
            onChange: ({ value }) => {
              const isValid = validate(value, 'dni');
              return isValid ? undefined : 'errors.dniInvalid';
            },
          }}
          // eslint-disable-next-line react/no-children-prop
          children={field => {
            return (
              <SignInput
                id={field.name}
                label={'pages.pagos.tutorCountryId'}
                placeholder=''
                type={'text'}
                // type={userSignData.tutorId === '' ? 'text' : 'hidden'}
                value={field.state.value}
                onChange={field.handleChange}
                onBlur={field.handleBlur}
                errorMessage={field.state.meta.errors.join(', ')}
              />
            );
          }}
        />

        <form.Field
          name={'patientCountryId'}
          validators={{
            onChange: ({ value }) => {
              const isValid = validate(value, 'dni');
              return isValid ? undefined : 'errors.dniInvalid';
            },
          }}
          // eslint-disable-next-line react/no-children-prop
          children={field => {
            return (
              <SignInput
                id={field.name}
                label={'pages.pagos.patientCountryId'}
                placeholder=''
                type={'text'}
                // type={userSignData.patientCountryId === '' ? 'text' : 'hidden'}
                value={field.state.value}
                onChange={field.handleChange}
                onBlur={field.handleBlur}
                errorMessage={field.state.meta.errors.join(', ')}
              />
            );
          }}
        />

        <form.Subscribe
          // @ts-ignore
          selector={state => [state.canSubmit, state.isSubmitting]}
          // @ts-ignore
          // eslint-disable-next-line react/no-children-prop
          children={([canSubmit, isSubmitting]) => (
            <Button
              styleType='primary'
              disabled={!canSubmit}
              fullWidth={true}
              type='submit'
              isLoading={isSubmitting || isRedirecting || isLoading}
            >
              {GlobalService.uppercaseFirstLetter(t('pages.pagos.formButtonLabel'))}
            </Button>
          )}
        />
      </form>
    </>
  ) : (
    <>{isLoadingSignForm ? <Loader /> : null}</>
  );
};
